import { useSelector } from 'react-redux'

import {
  currentProviderFeatures,
  userRoleSelector,
} from 'src/redux/providers/selectors'
import type { RouteAccessRule } from './routeAccess'
import { routeAccessAllowed } from './routeAccess'

// ----------------------------------------------------------------------
// Who may edit a vehicle.
//
// Three places asked this and each gave a different answer. The route behind
// `/admin/vehicles/:id/edit` required `vehicleCreateFormEnabled`, the flag for
// adding a car, so a provider whose plan includes editing and not creation was
// refused a screen they pay for. The Edit button on the vehicle page appeared
// for any provider admin whether or not editing was switched on, and the fleet
// list's menu offered Edit to every role once it was. The route was the one
// that refused, so both links led straight to the refusal.
//
// One rule now, asked through the gate's own predicate: the edit feature and a
// role that manages the fleet, with Beast staff admitted as they are
// everywhere else. The route spreads `VEHICLE_EDIT_ACCESS`, and the button and
// the menu ask `useVehicleEditAllowed`, so the three cannot disagree again.
//
// Creation keeps its own flag. Adding a car and changing one are different
// things a plan can include, and only the route that edits was wrong.
//
// The backend is unchanged by this: `vehicleUpdate` still checks that the car
// belongs to the provider and that the subscription is valid. This decides
// what the console offers, not what the API accepts.
// ----------------------------------------------------------------------

export const VEHICLE_EDIT_ACCESS = {
  requiredFeatureFlag: 'vehicleEditFormEnabled',
  requiredRole: ['admin', 'fleetManager'],
} as const satisfies RouteAccessRule

/** Whether the viewer may open the vehicle edit screen. */
export const useVehicleEditAllowed = (): boolean => {
  const role = useSelector(userRoleSelector)
  const features = useSelector(currentProviderFeatures)

  return routeAccessAllowed(VEHICLE_EDIT_ACCESS, role, features)
}
